import React, { useEffect, useState } from 'react';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import IssueCard from '../issues/IssueCard';

const SprintBoard = ({ sprint }) => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSprintIssues = async () => {
      try {
        const issuesQuery = query(
          collection(db, 'issues'),
          where('sprintId', '==', sprint.id)
        );
        const snapshot = await getDocs(issuesQuery);
        const issuesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setIssues(issuesData);
      } catch (error) {
        console.error('Error fetching sprint issues:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSprintIssues();
  }, [sprint.id]);

  const handleIssueUpdate = (updatedIssue) => {
    setIssues(prev => prev.map(issue => 
      issue.id === updatedIssue.id ? updatedIssue : issue
    ));
  };

  const statusColumns = [
    { key: 'todo', title: 'To Do', color: 'gray' },
    { key: 'in-progress', title: 'In Progress', color: 'blue' },
    { key: 'in-review', title: 'In Review', color: 'yellow' },
    { key: 'done', title: 'Done', color: 'green' }
  ];

  const getIssuesByStatus = (status) => {
    return issues.filter(issue => issue.status === status);
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="space-y-3">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="p-6 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
          {sprint.name} - Sprint Board
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          {sprint.goal}
        </p>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {statusColumns.map(column => (
            <div key={column.key} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className={`font-semibold text-${column.color}-700 dark:text-${column.color}-300`}>
                  {column.title}
                </h3>
                <span className="bg-white dark:bg-gray-600 px-2 py-1 rounded text-sm font-medium">
                  {getIssuesByStatus(column.key).length}
                </span>
              </div>
              
              <div className="space-y-3 min-h-[200px]">
                {getIssuesByStatus(column.key).map(issue => (
                  <IssueCard
                    key={issue.id}
                    issue={issue}
                    compact
                    onUpdate={handleIssueUpdate}
                  />
                ))}
                
                {getIssuesByStatus(column.key).length === 0 && (
                  <div className="text-center py-8 text-gray-400 dark:text-gray-500">
                    <div className="text-2xl mb-2">📋</div>
                    <p className="text-sm">No issues</p>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-blue-900 dark:text-blue-100">
                Sprint Progress
              </h4>
              <p className="text-blue-700 dark:text-blue-300 text-sm">
                {getIssuesByStatus('done').length} of {issues.length} issues completed
              </p>
            </div>
            
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                {issues.length > 0 ? Math.round((getIssuesByStatus('done').length / issues.length) * 100) : 0}%
              </div>
              <div className="text-sm text-blue-700 dark:text-blue-300">
                Complete
              </div>
            </div>
          </div>
          
          <div className="mt-3 w-full bg-blue-200 dark:bg-blue-700 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ 
                width: `${issues.length > 0 ? (getIssuesByStatus('done').length / issues.length) * 100 : 0}%` 
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SprintBoard;